<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, Container, Modal, toast } from '@Shelly/shelly-design-system';
	import { resetEditUpdateStore, update } from 'src/stores/ui/edit_lists';
	import { capFirstChar } from 'src/helpers/utilities';
	import BatchApplianceType from './BatchApplianceType.svelte';
	import BatchToggler from './BatchToggler.svelte';
	import BatchText from './BatchText.svelte';
	import { postReq } from 'src/services/http';
	import type { Device } from 'src/types/Device';
	import { devices } from 'src/stores/lists/devices';
	import { castUsageStringToRelayUsageParam } from 'src/helpers/device_settings_helpers';
	import PinUnlock from '../PinUnlock.svelte';
	import { NotifType } from 'src/types/DeviceNotifPermissions';
	import { eraseOrCancelAIModelBulk } from '../DevicesSettings/ApplicationSettings/AIModel/ai_helper';

	export let checked: boolean;
	export let showModal: boolean;
	export let roomConsumption: boolean;
	export let accountConsumption: boolean;
	export let areFiltersDisabled: boolean;

	let component: any = BatchApplianceType;
	let variable: any;
	let successMessage = '';
	let errorMessage = $format('setting_err_toast', {
		default: 'Failed to apply settings.',
	});
	let showPinCode = false;

	switch ($update.type) {
		case 'exclude_event_log':
			component = BatchToggler;
			variable = checked;
			successMessage = $format('event_log_scc_toast', {
				default: 'Event log exclusion settings changed.',
			});
			break;
		case 'consumption_calculation':
			component = BatchToggler;
			variable = { roomConsumption, accountConsumption };
			successMessage = $format('toast_success_consumption_calculation_msg', {
				default:
					'Devices have successfully changed their total room and account consumption settings.',
			});
			break;
		case 'exclude_forgotten_lights':
			component = BatchToggler;
			variable = checked;
			successMessage = $format('toast_succ_exclude_ai_forgotten', {
				default: '"Forgotten lights" notifications and e-mails settings changed.',
			});
			break;
		case 'ai_device_failure':
			if ($update.subType === 'manage_ai_notifications') {
				component = BatchToggler;
				variable = checked;
				successMessage = $format('toast_succ_exclude_ai_failure', {
					default: '"Failure detection" notifications and e-mails settings changed.',
				});
			} else {
				component = BatchText;
				variable = checked;
				successMessage =
					$update.subType === 'enable_ai_model'
						? $format('toast_succ_enable_ai_model', {
								default: 'Successfully enabled AI model for this devices.',
							})
						: $format('toast_succ_disable_ai_model', {
								default: 'Successfully disabled AI model for this devices.',
							});
			}
			break;
		case 'measure_peak_power':
			component = BatchToggler;
			variable = checked;
			successMessage = variable
				? $format('toast_success_enable_peak_measurement', {
						default: 'Devices have successfully enabled "Peak Power" measurements.',
					})
				: $format('toast_success_disable_peak_measurement', {
						default: 'Devices have successfully disabled "Peak Power" measurements.',
					});
			break;
		case 'pin_lock':
			component = BatchToggler;
			variable = checked;
			successMessage = variable
				? $format('enable_pin_lock_scc_toast', {
						default: 'PIN lock was enabled.',
					})
				: $format('disable_pin_lock_scc_toast', {
						default: 'PIN lock was disabled.',
					});
			break;
	}

	const getToggleVariable = (e) => {
		variable = e.detail;
		switch ($update.type) {
			case 'appliance_type':
				successMessage = $format('appliance_type_scc_toast', {
					values: { value: variable.title },
					default: `Appliance type was changed to ${variable.title}.`,
				});
				break;
			case 'consumption_calculation':
				variable = {
					roomConsumption: e.detail.roomConsumption,
					accountConsumption: e.detail.accountConsumption,
				};
				break;
			case 'measure_peak_power':
				successMessage = variable
					? $format('toast_success_enable_peak_measurement', {
							default: 'Devices have successfully enabled "Peak Power" measurements.',
						})
					: $format('toast_success_disable_peak_measurement', {
							default: 'Devices have successfully disabled "Peak Power" measurements.',
						});
				break;
			case 'pin_lock':
				successMessage = variable
					? $format('enable_pin_lock_scc_toast', {
							default: 'PIN lock was enabled.',
						})
					: $format('disable_pin_lock_scc_toast', {
							default: 'PIN lock was disabled.',
						});
				break;
		}
	};

	const submit = async () => {
		let selectedDevices: Device[] = Object.values($update.selectedDevices) as any;

		let res;
		if (
			['exclude_event_log', 'exclude_forgotten_lights', 'measure_peak_power'].includes(
				$update.type,
			)
		) {
			res = await postReq('interface/device/bulk_cloud_options', {
				set: $update.type,
				val: $update.type === 'exclude_forgotten_lights' ? !variable : variable,
				devices: JSON.stringify(selectedDevices.map((device) => device.id)),
			});
			if (res.isok) {
				selectedDevices.forEach((device) => {
					const updatedDevice = {
						...device,
						cloud_options: {
							...device.cloud_options,
							[$update.type]:
								$update.type === 'exclude_forgotten_lights' ? !variable : variable,
						},
						...(device.hasOwnProperty($update.type) && {
							[$update.type]:
								$update.type === 'exclude_forgotten_lights' ? !variable : variable,
						}),
					};
					devices.update(updatedDevice);
				});
			}
		} else if ($update.type === 'ai_device_failure') {
			if ($update.subType === 'manage_ai_notifications') {
				res = await postReq('interface/device/bulk_device_notifications', {
					devices: JSON.stringify(selectedDevices.map((device) => device.id)),
					set: 'ai_device_failure',
					val: variable ? 1 : 0,
					ntype: NotifType.MAIL,
				});
				if (res.isok) {
					res = await postReq('interface/device/bulk_device_notifications', {
						devices: JSON.stringify(selectedDevices.map((device) => device.id)),
						set: 'ai_device_failure',
						val: variable ? 1 : 0,
						ntype: NotifType.PUSH,
					});
					if (res.isok) {
						selectedDevices.forEach((device) => {
							const updatedDevice = {
								...device,
								notif_permissions: {
									...device.notif_permissions,
									ai_device_failure: Array(2).fill(variable ? 1 : 0),
								},
							};
							devices.update(updatedDevice);
						});
					}
				}
			} else if ($update.subType === 'enable_ai_model') {
				//bulk enable AI model
				res = await postReq('interface/device/bulk_cloud_options', {
					set: $update.type,
					val: true,
					devices: JSON.stringify(selectedDevices.map((device) => device.id)),
				});
				if (res.isok) {
					selectedDevices.forEach((device) => {
						const updatedDevice = {
							...device,
							cloud_options: {
								...device.cloud_options,
								ai_device_failure: true,
							},
						};
						devices.update(updatedDevice);
					});
				}
			} else if ($update.subType === 'disable_ai_model') {
				//bulk disable AI model
				res = await eraseOrCancelAIModelBulk(selectedDevices, $update.type);
			}
		} else {
			if ($update.type === 'pin_lock' && !variable && !showPinCode) {
				showPinCode = true;
				return;
			}

			const editedDevices = {};
			selectedDevices.forEach((device) => {
				editedDevices[device.id] =
					$update.type === 'consumption_calculation'
						? {
								no_room_cons: variable.roomConsumption,
								no_account_cons: variable.accountConsumption,
							}
						: $update.type === 'appliance_type'
							? {
									appliance_type: variable.value,
									relay_usage: castUsageStringToRelayUsageParam(variable),
								}
							: { pin_lock: variable };
			});

			res = await postReq(`interface/device/bulk_update`, {
				devices_data: JSON.stringify(editedDevices),
			});
			if (res.isok) {
				selectedDevices.forEach(async (device) => {
					const updatedDevice = {
						...device,
						...($update.type === 'consumption_calculation'
							? {
									no_room_cons: variable.roomConsumption,
									no_account_cons: variable.accountConsumption,
								}
							: $update.type === 'appliance_type'
								? {
										appliance_type: variable.value,
										relay_usage: castUsageStringToRelayUsageParam(variable),
									}
								: { pin_lock: variable }),
					};

					if ($update.type === 'appliance_type' && updatedDevice.relay_usage !== 'light') {
						const internalRes = await postReq('interface/device/set_cloud_options', {
							id: updatedDevice.id,
							set: 'exclude_forgotten_lights',
							val: true,
						});
						if (internalRes.isok) {
							updatedDevice.cloud_options.exclude_forgotten_lights = true;
						}
					}

					devices.update(updatedDevice);
				});
			}
		}

		if (res?.isok) {
			toast.open({
				type: 'success',
				title: successMessage,
			});
		} else {
			toast.open({
				type: 'danger',
				title: errorMessage,
			});
		}

		resetEditUpdateStore();
		areFiltersDisabled = false;
		showModal = false;
	};
</script>

<Modal
	on:close={() => (showModal = false)}
	{showModal}
	title={capFirstChar($format(`sub_settings_title_${$update.type}`))}
>
	<Container slot="content" padding="0">
		<svelte:component
			this={component}
			{checked}
			{roomConsumption}
			{accountConsumption}
			on:toggler={getToggleVariable}
		/>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={() => (showModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			on:click={submit}
			disabled={($update.type === 'appliance_type' && !variable) || false}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>

{#if showPinCode}
	<PinUnlock bind:showModal={showPinCode} on:unlock={submit} on:cancel={() => (showPinCode = false)} />
{/if}
